import { useMemo, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import {
  captureError,
  PlatformBasicError,
  useBaseAuthenticated,
  usePlatformExecuteAdHocQueryTeam,
} from 'shared/base';
import { assertUnreachable } from 'shared/base/utils/assertUnreachable';

import { FilterInputConfigError } from './filterInputConfigError';
import { FilterInputProps } from './types';

type Props = FilterInputProps & {
  options?: { label: string; value: any }[];
  query?: string;
};

interface FilterOption {
  expression: string;
  label: string;
  value: string;
}

const DEFAULT_FILTER_OPTION: FilterOption = {
  expression: '',
  label: 'All',
  value: 'All',
};

export const ReportGenericFilterSelect = ({
  filterExpression,
  filterLabel,
  onChange,
  options,
  query,
  selectedFilter,
}: Props) => {
  const { currentMembership } = useBaseAuthenticated();
  const teamName = currentMembership.team.name;
  const [error, setError] = useState<string>();
  const {
    data,
    error: errorData,
    status,
  } = usePlatformExecuteAdHocQueryTeam(
    teamName,
    {
      query: query ?? '',
    },
    {
      page: 1,
      per_page: 1000,
    },
  );

  const filterOptions = useMemo(() => {
    if (options) {
      return options.map((option) => ({
        expression: filterExpression?.replace('?', option.value as string),
        label: option.label,
        value: option.value,
      }));
    }

    switch (status) {
      case 'loading': {
        return [DEFAULT_FILTER_OPTION];
      }
      case 'error': {
        const { data: { message } = { message: '' } } = errorData as PlatformBasicError & {
          data: { message: string };
        };
        const missingTable = message.match(/Unknown table expression identifier '(.*?)'/);
        if (missingTable) {
          setError(`The ${missingTable[1]} table is missing.`);
        } else {
          setError(`Invalid query`);
        }

        captureError(`Platform reports error: Invalid filter query: ${message}`);

        return;
      }
      case 'success': {
        return [
          DEFAULT_FILTER_OPTION,
          ...data.data.data.rows.map(([value, name]) => ({
            expression: filterExpression?.replace('?', value as string),
            label: (name ?? value) as string,
            value: value as string,
          })),
        ];
      }
      default: {
        assertUnreachable(status);
      }
    }
  }, [options, status, filterExpression, errorData, data]);

  switch (status) {
    case 'error': {
      return <FilterInputConfigError filterLabel={filterLabel} message={error!} type={'generic'} />;
    }

    case 'loading':
    case 'success': {
      return (
        <FormControl>
          <Autocomplete
            getOptionLabel={(option) => option?.label || ''}
            onChange={(_, value) => {
              if (value === null || value.expression === null) {
                onChange(null);
              } else {
                onChange(value.expression);
              }
            }}
            options={filterOptions || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label={filterLabel}
                placeholder={filterLabel}
                size="small"
                sx={{
                  maxWidth: 160,
                }}
                variant="filled"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={props.key} style={{ fontSize: 12 }}>
                <Stack>{option.label}</Stack>
              </li>
            )}
            sx={{
              width: 160,
            }}
            value={filterOptions?.find((opt) => opt.expression === selectedFilter) || null}
          />
        </FormControl>
      );
    }
  }
};
